import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { equipmentData } from '../mockData';

export const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1652352568961-143b1ed17746?w=1920&q=80" 
            alt="Modern Hospital" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-blue-800/85 to-cyan-900/90"></div>
        </div>
        
        <div className="mx-auto max-w-7xl relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Equipamentos de Diagnóstico por Imagem
                <span className="block text-cyan-300 mt-2">Vendas, Locação e Manutenção</span>
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Fornecendo tecnologia de imagem médica de ponta e serviços de manutenção confiáveis para instalações de saúde.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/equipment">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg">
                  Ver Equipamentos
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg">
                  Solicitar Orçamento
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <Card className="border-2 hover:border-blue-600 transition-colors duration-300">
              <CardContent className="p-8 space-y-4">
                <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Award className="text-blue-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Vendas de Equipamentos</h3>
                <p className="text-gray-600">
                  Equipamentos de diagnóstico por imagem de distribuidores autorizados com instalação e treinamento completos.
                </p>
                <Link to="/sales">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Saiba Mais
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-blue-600 transition-colors duration-300">
              <CardContent className="p-8 space-y-4">
                <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="text-blue-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Locação de Equipamentos</h3>
                <p className="text-gray-600">
                  Flexibilidade e tecnologia de ponta sem grandes investimentos. Contratos flexíveis com manutenção incluída.
                </p>
                <Link to="/rental">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Saiba Mais
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-blue-600 transition-colors duration-300">
              <CardContent className="p-8 space-y-4">
                <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Clock className="text-blue-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Serviços de Manutenção</h3>
                <p className="text-gray-600">
                  Programas abrangentes de manutenção preventiva e corretiva para manter seus equipamentos funcionando.
                </p>
                <Link to="/maintenance">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Saiba Mais
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-blue-600 transition-colors duration-300">
              <CardContent className="p-8 space-y-4">
                <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-blue-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Consultoria</h3>
                <p className="text-gray-600">
                  Orientação especializada para escolher as melhores soluções em equipamentos de diagnóstico por imagem.
                </p>
                <Link to="/contact">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Fale Conosco
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Equipment Preview */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Nossa Linha de Equipamentos
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Equipamentos de imagem médica de última geração
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {equipmentData.map((equipment) => (
              <Card key={equipment.id} className="hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                <div className="h-48 overflow-hidden bg-gray-100">
                  <img 
                    src={equipment.image} 
                    alt={equipment.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6 space-y-4">
                  <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="text-blue-600" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{equipment.name}</h3>
                  <p className="text-sm text-gray-600">{equipment.description}</p>
                  <Link to="/equipment">
                    <Button variant="link" className="text-blue-600 p-0 h-auto">
                      Saiba Mais <ArrowRight size={16} className="ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Pronto para Modernizar sua Instalação Médica?
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Entre em contato hoje para uma consulta personalizada e orçamento
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Comece Hoje
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};